import { Text } from '@shopify/polaris';
import { Area, AreaChart, CartesianGrid, ReferenceDot, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { AXIS_TICK, COLORS, formatAxisCurrency } from '../analytics/components/dashboard/chartTheme';
import { formatToCurrency } from '../../shared/components/functions.jsx';

/*
  Cash in hand over one year: a step per cashbook entry, from the opening balance (1 January, or the day the counted
  opening balance starts) to now or the year end. Points come from GET /api/cashbook/balance; dates are wall-clock
  strings, plotted as UTC so no timezone shifts a step onto another day.
*/

const toTime = (wallClock) => new Date(`${wallClock.length === 10 ? `${wallClock}T00:00:00` : wallClock}Z`).getTime();

const monthTick = (time) => new Date(time).toLocaleDateString('en-GB', { month: 'short', timeZone: 'UTC' });

export const formatWallClockDate = (wallClock, withTime = false) =>
  new Date(toTime(wallClock)).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
    ...(withTime && wallClock.length > 10 ? { hour: '2-digit', minute: '2-digit' } : {})
  });

// first day of every month inside the domain
function monthTicks(from, to) {
  const ticks = [];
  const date = new Date(from);
  date.setUTCDate(1);
  date.setUTCHours(0, 0, 0, 0);
  if (date.getTime() < from) date.setUTCMonth(date.getUTCMonth() + 1);
  while (date.getTime() <= to) {
    ticks.push(date.getTime());
    date.setUTCMonth(date.getUTCMonth() + 1);
  }
  return ticks;
}

// 0 up to the highest balance in four or five round steps (500, 1000, 2500 …): the cash box holds a few thousand euro,
// so the shared €k axis format would print €1.350 as "€1k"
function balanceTicks(max) {
  const raw = Math.max(max, 400) / 4;
  const magnitude = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((factor) => factor * magnitude).find((candidate) => candidate >= raw);
  return Array.from({ length: Math.max(1, Math.ceil(max / step)) + 1 }, (_, index) => index * step);
}

const balanceTick = (value) => (Math.abs(value) >= 10000 ? formatAxisCurrency(value) : `€${Math.round(value).toLocaleString('nl-BE')}`);

function EntryDot({ cx, cy, payload }) {
  if (payload?.kind !== 'entry' || cx == null || cy == null) return null;
  return <circle cx={cx} cy={cy} r={4} fill={COLORS.cash} stroke={COLORS.surface} strokeWidth={2} />;
}

function BalanceTooltip({ active, payload }) {
  const point = active && payload?.[0]?.payload;
  if (!point) return null;
  const heading = point.kind === 'opening' ? 'Opening balance' : point.kind === 'now' ? 'Now' : point.kind === 'close' ? 'Closing balance' : `Entry ${point.number}`;
  return (
    <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.grid}`, borderRadius: 8, padding: '8px 10px', boxShadow: '0 4px 12px rgba(0,0,0,0.08)', maxWidth: 280 }}>
      <div style={{ fontSize: 12, color: COLORS.axis }}>
        {heading} · {formatWallClockDate(point.date, point.kind === 'entry')}
      </div>
      <div style={{ fontSize: 15, fontWeight: 600, color: COLORS.ink, marginTop: 2 }}>{formatToCurrency(point.balance)}</div>
      {point.kind === 'entry' ? (
        <div style={{ fontSize: 12, color: COLORS.ink, marginTop: 4 }}>
          <span style={{ fontWeight: 600 }}>
            {point.amount > 0 ? '+' : ''}
            {formatToCurrency(point.amount)}
          </span>{' '}
          {point.description}
        </div>
      ) : null}
    </div>
  );
}

// the current (or closing) balance written next to the last step
function EndLabel({ viewBox, value }) {
  if (!viewBox) return null;
  return (
    <text x={viewBox.x - 8} y={viewBox.y - 10} textAnchor="end" fontSize={12} fontWeight={600} fill={COLORS.ink}>
      {value}
    </text>
  );
}

export default function CashBalanceChart({ points }) {
  const data = points.map((point) => ({ ...point, t: toTime(point.date) }));
  const from = data[0].t;
  const to = data[data.length - 1].t;
  const last = data[data.length - 1];
  const yTicks = balanceTicks(Math.max(...data.map((point) => point.balance)));

  return (
    <div style={{ width: '100%', height: 280 }} role="img" aria-label={`Cash balance from ${formatToCurrency(data[0].balance)} to ${formatToCurrency(last.balance)}`}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 28, right: 12, left: 4, bottom: 0 }}>
          <CartesianGrid vertical={false} stroke={COLORS.grid} />
          <XAxis
            dataKey="t"
            type="number"
            scale="time"
            domain={[from, to]}
            ticks={monthTicks(from, to)}
            tickFormatter={monthTick}
            tick={AXIS_TICK}
            tickLine={false}
            axisLine={{ stroke: COLORS.grid }}
          />
          <YAxis
            domain={[Math.min(0, ...data.map((point) => point.balance)), yTicks[yTicks.length - 1]]}
            ticks={yTicks}
            tickFormatter={balanceTick}
            tick={AXIS_TICK}
            tickLine={false}
            axisLine={false}
            width={56}
          />
          <Tooltip content={<BalanceTooltip />} cursor={{ stroke: COLORS.axis, strokeWidth: 1 }} isAnimationActive={false} />
          <Area
            type="stepAfter"
            dataKey="balance"
            stroke={COLORS.cash}
            strokeWidth={2}
            fill={COLORS.cash}
            fillOpacity={0.08}
            dot={<EntryDot />}
            activeDot={{ r: 5, fill: COLORS.cash, stroke: COLORS.surface, strokeWidth: 2 }}
            isAnimationActive={false}
          />
          <ReferenceDot x={last.t} y={last.balance} r={0} label={<EndLabel value={formatToCurrency(last.balance)} />} ifOverflow="visible" />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

export function CashBalanceChartCaption({ opening }) {
  return (
    <Text as="p" variant="bodySm" tone="subdued">
      Cash in hand after every entry, starting from the counted {formatToCurrency(opening.amount)} on {formatWallClockDate(opening.date)}. Hover a step to see the entry behind it.
    </Text>
  );
}
